import { FileSymlinkFileIcon, SearchIcon, SyncIcon } from '@primer/octicons-react';
import { useRouter } from 'next/router';
import React, { Suspense } from 'react';
import { graphql } from 'react-relay';

import AlimTalkTemplateConnectionDataTable from '../../components/alimTalk/AlimTalkTemplateConnectionDataTable';
import AlimTalkTemplateWindowPaginator from '../../components/alimTalk/AlimTalkTemplateWindowPaginator';
import Button from '../../components/core/Button';
import EmptyState from '../../components/core/EmptyState';
import ErrorBoundary from '../../components/core/ErrorBoundary';
import Grid from '../../components/core/Grid';
import Head from '../../components/core/Head';
import { HeaderSidebarNavPageLayout } from '../../components/core/Layout';
import QueryFormik from '../../components/core/QueryFormik';
import Spinner from '../../components/core/Spinner';
import Stack from '../../components/core/Stack';
import Text from '../../components/core/Text';
import View from '../../components/core/View';
import { usePaginationContext } from '../../contexts/PaginationContext';
import useInitialValuesFromParsedUrlQuery from '../../hooks/useInitialValuesFromParsedUrlQuery';
import { alimTalkTemplate_alimTalkTemplatesQuery } from '../../relay/__generated__/alimTalkTemplate_alimTalkTemplatesQuery.graphql';
import { numberWithCommas } from '../../utils/number';
import { NextPage } from '../_app';

import AlimTalkTemplateId from './[alimTalkTemplateId]';

const alimTalkTemplatesForAlimTalkTemplate = graphql`
  query alimTalkTemplate_alimTalkTemplatesQuery(
    $filters: AlimTalkTemplateFilter
    $order: AlimTalkTemplateOrder
    $page: Int
    $pageSize: Int
  ) {
    ...AlimTalkTemplateWindowPaginator_query
      @arguments(filters: $filters, order: $order, page: $page, pageSize: $pageSize)
  }
`;

type Props = {};

const AlimTalkTemplate: NextPage<Props> = () => {
  const router = useRouter();
  const { initialValues, setParsedUrlQuery } = useInitialValuesFromParsedUrlQuery({
    search: { type: 'string' },
    order: { type: 'string' },
    page: { type: 'number' },
  });
  const { pageSize } = usePaginationContext();

  const { search, order, page } = initialValues;

  return (
    <View>
      <Head siteTitle={'알림톡 템플릿'} />
      <ErrorBoundary>
        <QueryFormik<alimTalkTemplate_alimTalkTemplatesQuery>
          query={alimTalkTemplatesForAlimTalkTemplate}
          staticVariables={{ pageSize }}
          initialValues={{
            filters: {
              search,
              isDormant_Exact: false,
            },
            order: order || '-createTime',
            page,
          }}
          options={{ fetchPolicy: 'store-and-network' }}
          onSubmit={(values) => setParsedUrlQuery({ ...values.filters, order }, { scroll: false })}
        >
          {({ values: { filters, order }, resetForm, setFieldValue, submitForm }, queryReference) => {
            const handleReset = () => {
              resetForm({ values: { order } });
              setParsedUrlQuery({ order }, { scroll: false });
            };

            return (
              <View>
                <Grid sx={{ alignItems: 'center' }}>
                  <Grid.Unit size={'max'}>
                    <Text as={'h1'}>알림톡 템플릿</Text>
                  </Grid.Unit>
                </Grid>
                <Grid>
                  <Grid.Unit size={'min'}>
                    <QueryFormik.FilterSearchTextField
                      typename={'AlimTalkTemplateFilter'}
                      label={'Search'}
                      labelConfig={{ visuallyHidden: true }}
                      name={'filters.search'}
                      size={'large'}
                      autoComplete={'off'}
                      leadingVisual={SearchIcon}
                      debounce
                      onChange={(e) => {
                        setFieldValue('filters.search', e.target.value);
                        setTimeout(() => submitForm(), 0);
                      }}
                    />
                  </Grid.Unit>
                  <Grid.Unit size={'max'}>
                    <View sx={{ display: 'flex', justifyContent: 'end' }}>
                      <Stack gapX={2}>
                        <Stack.Item>
                          <Button leadingIcon={SyncIcon} variant={'plain'} onClick={handleReset}>
                            초기화
                          </Button>
                        </Stack.Item>
                      </Stack>
                    </View>
                  </Grid.Unit>
                </Grid>
                <View sx={{ marginTop: 5 }}>
                  <ErrorBoundary key={queryReference?.fetchKey}>
                    <Suspense fallback={<Spinner />}>
                      <QueryFormik.PreloadedQueryRenderer<alimTalkTemplate_alimTalkTemplatesQuery>>
                        {(queryReference) => (
                          <AlimTalkTemplateWindowPaginator
                            fragmentReference={queryReference}
                            onLoadPage={(page) => setParsedUrlQuery({ ...filters, order, page })}
                          >
                            {({ alimTalkTemplates }, { renderPagination }) => (
                              <>
                                <View>
                                  <Text sx={{ fontSize: 1, fontWeight: 'bold', color: 'fg.muted' }}>
                                    총 {numberWithCommas(alimTalkTemplates?.totalCount || 0)}건
                                  </Text>
                                </View>
                                <View sx={{ marginTop: 3 }}>
                                  <AlimTalkTemplateConnectionDataTable
                                    alimTalkTemplateConnection={alimTalkTemplates}
                                    onRowClick={({ id }) => router.push({ pathname: `/alimTalkTemplate/${id}` })}
                                    emptyState={
                                      <View sx={{ paddingY: 3 }}>
                                        <EmptyState
                                          title={'필터에 맞는 결과가 없어요'}
                                          description={'다른 필터로 다시 시도해보세요.'}
                                        />
                                      </View>
                                    }
                                  />
                                </View>
                                <View sx={{ display: 'flex', justifyContent: 'center', marginTop: 3 }}>
                                  {renderPagination?.()}
                                </View>
                              </>
                            )}
                          </AlimTalkTemplateWindowPaginator>
                        )}
                      </QueryFormik.PreloadedQueryRenderer>
                    </Suspense>
                  </ErrorBoundary>
                </View>
              </View>
            );
          }}
        </QueryFormik>
      </ErrorBoundary>
    </View>
  );
};

AlimTalkTemplate.getLayout = (page) => <HeaderSidebarNavPageLayout>{page}</HeaderSidebarNavPageLayout>;
AlimTalkTemplate.authenticated = true;
AlimTalkTemplate.routes = [
  {
    id: 'alimTalkTemplate',
    icon: FileSymlinkFileIcon,
    pathname: '/alimTalkTemplate',
    name: '알림톡 템플릿',
    permissions: ['app_push_read'],
  },
  ...AlimTalkTemplateId.routes,
];

export default AlimTalkTemplate;
